import { useEffect, useState } from "react";
import { format, parse } from "date-fns";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Loading } from "../../../../../../_metronic/elements/Loading";
import { Modal } from "../../../../../../_metronic/elements/Modal";
import { openAlert } from "../../../../../../_metronic/elements/NotificationAlert";
import { KTSVG } from "../../../../../../_metronic/helpers";
import { useViewNDVIContext, ViewNDVIProvider } from "./ViewNDVIContext";
import ModalBody from "./ModalBody";
import DeleteReport from "./DeleteReport";
import { downloadNdviReportPdf } from "./ndviReportPdf";
import { useIsMobile } from "../../../../../shared/responsive";
import { getNdviReportSourceLabel } from "../ndviReportSource";
import "./ViewNDVI.scss";

const getPdfLabels = (t) => ({
  aggregateDistributionTitle: t("reports:ndvi.view.pdf.aggregate_distribution_title"),
  allDatesTitle: t("reports:ndvi.view.pdf.all_dates_title"),
  avgNdvi: t("reports:ndvi.view.pdf.avg_ndvi"),
  class: t("reports:ndvi.view.pdf.class"),
  classSummary: t("reports:ndvi.view.pdf.class_summary"),
  date: t("reports:ndvi.view.pdf.date"),
  distributionForDate: t("reports:ndvi.view.pdf.distribution_for_date"),
  distributionEvolutionTitle: t("reports:ndvi.view.pdf.distribution_evolution_title"),
  eyebrow: t("reports:ndvi.view.pdf.eyebrow"),
  finalDate: t("reports:ndvi.view.reports.ranking.final_date"),
  finalPosition: t("reports:ndvi.view.pdf.final_position"),
  fields: t("reports:technical.preview.fields"),
  generatedAt: t("reports:technical.preview.generated_at"),
  historyTitle: t("reports:ndvi.view.menu.ndvi_avg"),
  imageDate: t("reports:ndvi.view.pdf.image_date"),
  imagesUsed: t("reports:ndvi.view.reports.accumulated_per_date.images_used"),
  includedLandsTitle: t("reports:ndvi.view.pdf.included_lands_title"),
  initialDate: t("reports:ndvi.view.reports.ranking.initial_date"),
  initialPosition: t("reports:ndvi.view.pdf.initial_position"),
  land: t("reports:ndvi.view.reports.ranking.columns.land"),
  landAnalysisTitle: t("reports:ndvi.view.menu.land"),
  lands: t("reports:technical.preview.lands"),
  latestDistributionTitle: t("reports:ndvi.view.pdf.latest_distribution_title"),
  ndviAverage: t("reports:ndvi.view.reports.land.avg_ndvi"),
  ndviFinalDate: t("reports:ndvi.view.pdf.ndvi_final_date"),
  ndviInitialDate: t("reports:ndvi.view.pdf.ndvi_initial_date"),
  noData: t("reports:ndvi.view.pdf.no_data"),
  percent: t("reports:ndvi.view.pdf.percent"),
  period: t("reports:technical.preview.period"),
  position: t("reports:ndvi.view.reports.ranking.columns.position"),
  rankingDescription: t("reports:ndvi.view.pdf.ranking_description"),
  rankingTitle: t("reports:ndvi.view.pdf.ranking_title"),
  rankingVariation: t("reports:ndvi.view.pdf.ranking_variation"),
  surface: t("reports:technical.preview.surface"),
  source: t("reports:ndvi.view.pdf.source"),
  title: t("reports:selector.ndvi_report"),
  variation: t("reports:ndvi.view.pdf.variation"),
});

const DownloadPdfButton = ({ mobile = false }) => {
  const { t } = useTranslation();
  const context = useViewNDVIContext();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading || !context.report) {
      return;
    }

    setDownloading(true);

    try {
      await downloadNdviReportPdf({
        labels: getPdfLabels(t),
        report: context.report,
      });
    } catch (error) {
      console.error(error);
      openAlert(
        "danger",
        t("reports:ndvi.view.pdf.error_title"),
        t("reports:ndvi.view.pdf.error_message")
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      className={mobile ? "btn btn-sm btn-primary reports-mobile-card__primary-action" : "btn btn-sm btn-light-primary ms-2 ndvi-report-header__action ndvi-report-header__download"}
      onClick={handleDownload}
      disabled={downloading}
    >
      {downloading
        ? t("reports:ndvi.view.buttons.downloading_pdf")
        : t("reports:ndvi.view.buttons.download_pdf")}
    </button>
  );
};

const DesktopHeader = () => {
  const { t } = useTranslation();
  const context = useViewNDVIContext();
  const name = context.report.name
  const from_date = format(parse(context.report.from_date, "yyyy-LL-dd", new Date()), "dd/LL/yyyy");
  const to_date = format(parse(context.report.to_date, "yyyy-LL-dd", new Date()), "dd/LL/yyyy")
  const reportTitle = t("reports:selector.ndvi_report");
  const sourceLabel = getNdviReportSourceLabel(context.report);

  return (
    <div className="modal-header p-5 ndvi-report-header">
      <div className="ndvi-report-header__title-block">
        <h5 className="modal-title ndvi-report-header__title">{reportTitle}</h5>
        <div className="ndvi-report-header__subtitle">{name} - {from_date} a {to_date}</div>
        {sourceLabel && <div className="badge badge-light-primary mt-2">{sourceLabel}</div>}
      </div>
      <div className="ndvi-report-header__actions">
        <DownloadPdfButton />
        <button
          type="button"
          className="btn btn-sm btn-active-light-danger ms-2 ndvi-report-header__action"
          onClick={() => context.setDeleteReport(true)}
        >
          {t("reports:ndvi.delete.button")}
        </button>
        <div className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={() => context.close()} aria-label="Close">
          <KTSVG
            path="/media/icons/duotune/arrows/arr061.svg"
            className="svg-icon svg-icon-2x"
          />
        </div>
      </div>
    </div>
  );
};

const DesktopContent = () => {
  return (
    <>
      <DesktopHeader />
      <ModalBody />
      <DeleteReport />
    </>
  );
};

const MobileContent = () => {
  const { t } = useTranslation();
  const context = useViewNDVIContext();
  const name = context.report.name;
  const fromDate = format(parse(context.report.from_date, "yyyy-LL-dd", new Date()), "dd/LL/yyyy");
  const toDate = format(parse(context.report.to_date, "yyyy-LL-dd", new Date()), "dd/LL/yyyy");
  const sourceLabel = getNdviReportSourceLabel(context.report);

  return (
    <div className="reports-mobile-shell displayable">
      <div className="card shadow-sm right-card-data reports-mobile-card reports-mobile-card--detail">
        <div className="reports-mobile-card__header">
          <button
            type="button"
            className="btn btn-sm btn-light reports-mobile-card__back"
            onClick={() => context.close()}
          >
            <KTSVG
              path="/media/icons/duotune/arrows/arr063.svg"
              className="svg-icon svg-icon-2"
            />
            <span>{t("common:navigation.back_reports")}</span>
          </button>
          <div className="reports-mobile-card__eyebrow">{t("reports:selector.report_label")}</div>
          <h2 className="reports-mobile-card__title">{name}</h2>
          <div className="reports-mobile-card__meta">
            <span>{fromDate}</span>
            <span>-</span>
            <span>{toDate}</span>
            {sourceLabel && <span>{sourceLabel}</span>}
          </div>
          <DownloadPdfButton mobile />
          <button
            type="button"
            className="btn btn-sm btn-light-danger reports-mobile-card__danger-action"
            onClick={() => context.setDeleteReport(true)}
          >
            {t("reports:ndvi.delete.button")}
          </button>
        </div>
        <div className="reports-mobile-card__body reports-mobile-card__body--detail">
          <ModalBody mobile />
        </div>
      </div>
      <DeleteReport />
    </div>
  );
};

const ViewNDVIModule = () => {
  const { loading, load, report, close } = useViewNDVIContext();
  const { id } = useParams();
  const isMobile = useIsMobile();
  useEffect(() => {
    load(id);
  }, [id]);

  if (isMobile) {
    if (loading) {
      return (
        <div className="reports-mobile-shell displayable">
          <div className="card shadow-sm right-card-data reports-mobile-card">
            <div className="reports-mobile-card__body">
              <Loading />
            </div>
          </div>
        </div>
      );
    }

    if (!report) {
      return null;
    }

    return <MobileContent />;
  }

  return (
    <Modal open={true} setOpen={close}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className={"modal-content " + (loading ? "p-5" : "")}>
          {loading && <Loading />}
          {!loading && report && <DesktopContent />}
        </div>
      </div>
    </Modal>
  );
};

const ViewNDVI = () => {

  return (
    <ViewNDVIProvider>
      <ViewNDVIModule />
    </ViewNDVIProvider>
  )
}

export default ViewNDVI;
